import { useEffect, useRef, useState } from 'react';
import { Send, Bot, User, HelpCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API_BASE } from '../../utils/api-base';

interface ChatBotProps {
  clientId: string;
  token: string;
}

interface Message {
  role: 'user' | 'bot';
  text: string;
  matched?: boolean;
}

const WELCOME_MESSAGE = [
  "Hi! I can answer a few specific kinds of questions about your data:",
  "• Energy consumption (\"what was the energy consumption on March 5th\")",
  "• Uptime/downtime (\"was [device name] down yesterday\")",
  "• Active alerts (\"any alerts right now\")",
  "• Work orders (\"how many work orders are overdue\")",
  "• Peak demand (\"what was the peak demand yesterday\")",
  "I'm a rule-based assistant, not a full conversational AI — I work best when a question closely matches one of these patterns, and I'll say so if I don't understand.",
].join('\n');

export function ChatBot({ clientId, token }: ChatBotProps) {
  const [messages, setMessages] = useState<Message[]>([{ role: 'bot', text: WELCOME_MESSAGE }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const question = input.trim();
    if (!question || sending) return;
    setMessages((prev) => [...prev, { role: 'user', text: question }]);
    setInput('');
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/chatbot/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question }),
      });
      const payload = await res.json();
      if (res.ok && payload?.success) {
        setMessages((prev) => [...prev, { role: 'bot', text: payload.answer, matched: payload.matched }]);
      } else {
        setMessages((prev) => [...prev, { role: 'bot', text: payload?.error || "Sorry, I couldn't process that." }]);
      }
    } catch (_) {
      setMessages((prev) => [...prev, { role: 'bot', text: 'Something went wrong reaching the server. Try again in a moment.' }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 h-full flex flex-col max-w-3xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2"><Bot className="h-5 w-5" /> Chat Bot</h2>
        <p className="text-sm text-muted-foreground">Ask about energy usage, uptime, alerts, or maintenance — answered from your real data.</p>
      </div>

      <div ref={scrollRef} className="flex-1 border rounded-lg p-4 space-y-3 overflow-y-auto min-h-[420px] max-h-[520px]">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              {m.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>
            <div className={`rounded-lg px-3 py-2 text-sm max-w-[80%] whitespace-pre-line ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              {m.text}
              {m.role === 'bot' && m.matched === false && (
                <p className="text-xs opacity-70 mt-1 flex items-center gap-1"><HelpCircle className="h-3 w-3" /> I didn't recognize that as one of my known question types.</p>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex gap-2">
            <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 bg-muted text-muted-foreground">
              <Bot className="h-4 w-4" />
            </div>
            <div className="rounded-lg px-3 py-2 text-sm bg-muted text-muted-foreground">Thinking...</div>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-3">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="e.g. What was the energy consumption yesterday?"
          disabled={sending}
        />
        <Button onClick={send} disabled={sending || !input.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
